import { useState, useEffect } from 'react';

/**
 * Small corner indicator showing socket-connection health (not session status).
 * Green  = connected (collapsed to just a dot after 2s)
 * Yellow = reconnecting (pulsing)
 * Red    = disconnected (always labeled, tappable to force reconnect)
 *
 * Fixed to bottom-right; unobtrusive when all is well, informative when not.
 */
const ConnectionStatus = ({ isConnected, reconnecting, onReconnect }) => {
  const [expanded, setExpanded] = useState(false);

  // Auto-collapse the "Live" label a couple seconds after a successful connect
  useEffect(() => {
    if (isConnected && !reconnecting) {
      setExpanded(true);
      const t = setTimeout(() => setExpanded(false), 2000);
      return () => clearTimeout(t);
    }
    // Always expanded while reconnecting or offline
    setExpanded(true);
    return undefined;
  }, [isConnected, reconnecting]);

  let color;
  let label;
  let title;
  if (isConnected) {
    color = '#10b981'; // green-500
    label = 'Connected';
    title = 'Connected to server';
  } else if (reconnecting) {
    color = '#f59e0b'; // amber-500
    label = 'Reconnecting';
    title = 'Trying to reconnect...';
  } else {
    color = '#ef4444'; // red-500
    label = 'Offline';
    title = 'Tap to reconnect';
  }

  const handleClick = () => {
    if (!isConnected && !reconnecting && onReconnect) {
      onReconnect();
    }
    setExpanded(true);
    setTimeout(() => {
      if (isConnected) setExpanded(false);
    }, 2500);
  };

  return (
    <button
      onClick={handleClick}
      aria-label={title}
      title={title}
      style={{
        position: 'fixed',
        bottom: '80px', // above the bottom nav (56px + buffer)
        right: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'rgba(255, 255, 255, 0.96)',
        borderRadius: '9999px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        padding: expanded ? '6px 12px' : '6px',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        zIndex: 60,
        transition: 'padding 0.2s ease',
        cursor: isConnected ? 'default' : 'pointer',
      }}
    >
      <span
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: color,
          animation: reconnecting ? 'connection-pulse 1s ease-in-out infinite' : 'none',
          flexShrink: 0,
        }}
      />
      {expanded && (
        <span
          style={{
            fontSize: '12px',
            fontWeight: 500,
            color: '#334155',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      )}
      <style>{`
        @keyframes connection-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </button>
  );
};

export default ConnectionStatus;
